"use client";

import { ShellLink } from "@/src/components";
import React from "react";
import { cn } from "utils";
import dayjs from "dayjs";
import { ArticleCardProps } from "./types";
import { NextImage } from "@/src/components/NextImage";
import { ROUTER_PATH, config } from "@/src/config";
import { useBridge } from "@/src/lib/bridge";

/**
 * ArticleCard 组件 - 用于展示新闻文章卡片
 *
 * 显示文章标题、描述、作者信息和缩略图
 */
export const ArticleCard: React.FC<ArticleCardProps> = ({
  locale,
  title,
  description,
  authorDisplayName,
  handle,
  date,
  source,
  imageUrl,
  className,
  imgClassName,
  imgWidth,
  imgHeight,
}) => {
  // 格式化日期
  const formattedDate =
    typeof date === "number" ? dayjs(date).format("MMM D YYYY") : date;
  const { openUrl } = useBridge();
  const localePrefix = locale && locale !== "en" ? `/${locale}` : "";
  const safeSource = source ? encodeURIComponent(source) : "";
  const articleHref = `${config.externalLink?.aiChat || ""}${localePrefix}/article/${safeSource}`;
  const authorHref =
    handle && handle.trim()
      ? `${config.externalLink?.aiChat || ""}${localePrefix}${ROUTER_PATH.ARTICLE_AUTHOR_DETAIL}/${handle}`
      : "";

  return (
    <div
      data-openreplay-label="ArticleCard · ArticleCard · div"
      role="link"
      className={cn(
        "relative flex gap-8 items-start w-full transition-opacity",
        safeSource ? "cursor-pointer md:hover:opacity-80" : "cursor-default",
        className,
      )}
      data-component="ArticleCard"
      onClick={() => {
        if (!safeSource) {
          return;
        }
        openUrl(articleHref);
      }}
    >
      {/* 左侧内容容器 */}
      <div className="relative flex flex-col gap-3 flex-1 min-w-0">
        {/* 文章标题 */}
        <div className="flex flex-col gap-1">
          <h2
            aria-label="Article Card h2"
            data-openreplay-label="Article Card h2 Semantic"
            id="sem-article-card-h2"
            className="body_medium_l text-content_primary line-clamp-2"
          >
            {title}
          </h2>

          {/* 文章描述 */}
          {description ? (
            <p className="body_m text-content_secondary line-clamp-2 max-md:hidden">
              {description}
            </p>
          ) : null}
        </div>

        {/* 作者和日期信息 */}
        <div className="flex gap-1 items-center">
          {authorHref ? (
            <ShellLink
              aria-label="Article Card"
              data-openreplay-label={`Article Card Link | ${authorHref} | ${authorDisplayName || handle}`}
              id="lnk-article-card"
              href={authorHref}
              target="_blank"
              rel="author"
              className="relative body_medium_s text-content_primary whitespace-nowrap md:hover:opacity-80"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                openUrl(authorHref);
              }}
            >
              {authorDisplayName || handle}
            </ShellLink>
          ) : (
            <span className="relative body_medium_s text-content_primary whitespace-nowrap">
              {authorDisplayName || "—"}
            </span>
          )}
          <span className="body_s text-content_secondary">•</span>
          <span className="body_s text-content_secondary whitespace-nowrap">
            {formattedDate}
          </span>
        </div>
      </div>

      {/* 右侧图片容器 */}
      <div className="relative">
        <NextImage
          src={imageUrl}
          alt={title}
          width={imgWidth ?? 260}
          height={imgHeight ?? 146}
          className={cn("object-cover w-full h-full", imgClassName)}
        />
      </div>
      {/* </div> */}
    </div>
  );
};
